import { useEffect, useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import {
  getCommunity, joinCommunity, leaveCommunity, postToCommunity, commentOnPost, listComments,
  type Community as C, type CommunityPost, type CommunityComment,
} from "../lib/communities";

export function Community() {
  const { slug } = useParams();
  const [community, setCommunity] = useState<C | null>(null);
  const [joined, setJoined] = useState(false);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [openPost, setOpenPost] = useState<string | null>(null);
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [reply, setReply] = useState("");

  async function refresh() {
    if (!slug) return;
    const d = await getCommunity(slug);
    setCommunity(d.community);
    setJoined(d.joined);
    setPosts(d.posts);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void refresh(); }, [slug]);

  async function toggleJoin() {
    if (!community) return;
    setBusy(true);
    try {
      if (joined) await leaveCommunity(community.id);
      else await joinCommunity(community.id);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function submitPost(e: FormEvent) {
    e.preventDefault();
    if (!community || !body.trim()) return;
    setBusy(true);
    try {
      await postToCommunity(community.id, body);
      setBody("");
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function openComments(postId: string) {
    if (openPost === postId) { setOpenPost(null); return; }
    setOpenPost(postId);
    setComments((await listComments(postId)).comments);
  }

  async function submitComment(e: FormEvent, postId: string) {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await commentOnPost(postId, reply);
      setReply("");
      setComments((await listComments(postId)).comments);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (!community) return <p className="p-6 font-mono text-sm text-muted">Loading…</p>;

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader
        title={community.name}
        subtitle={community.description ?? undefined}
        actions={
          <Button variant="ghost" disabled={busy} onClick={toggleJoin}>{joined ? "Leave" : "Join"}</Button>
        }
      />
      <p className="font-mono text-[11px] text-muted"><Link to="/communities" className="text-accent-strong">← All communities</Link> · {community.member_count} members</p>

      {joined ? (
        <Panel title="Start a discussion">
          <form className="grid gap-2" onSubmit={submitPost}>
            <textarea className={inputClass} rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share something with the group…" />
            <Button type="submit" disabled={busy || !body.trim()}>Post</Button>
          </form>
        </Panel>
      ) : (
        <p className="rounded-lg border border-line bg-surface-2 p-3 text-sm text-muted">Join this community to post and comment.</p>
      )}

      {posts.length === 0 ? (
        <p className="text-sm text-muted">No posts yet. Be the first to start a discussion.</p>
      ) : (
        <ul className="grid gap-3">
          {posts.map((p) => (
            <Panel key={p.id}>
              <p className="whitespace-pre-wrap text-sm">{p.body}</p>
              <p className="font-mono text-[11px] text-muted">{p.author_name ?? "A member"} · {new Date(p.created_at).toLocaleString()}</p>
              <button type="button" className="w-max font-mono text-[11px] font-bold text-accent-strong" onClick={() => openComments(p.id)}>
                {p.comment_count} comment{p.comment_count === 1 ? "" : "s"}
              </button>
              {openPost === p.id && (
                <div className="grid gap-2 border-t border-line pt-3">
                  {comments.map((c) => (
                    <div key={c.id} className="text-sm">
                      <span className="text-muted">{c.body}</span>
                      <span className="block font-mono text-[10px] text-muted">{c.author_name ?? "A member"}</span>
                    </div>
                  ))}
                  {joined && (
                    <form className="flex gap-2" onSubmit={(e) => submitComment(e, p.id)}>
                      <input className={inputClass} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a comment…" />
                      <Button type="submit" disabled={busy || !reply.trim()}>Reply</Button>
                    </form>
                  )}
                </div>
              )}
            </Panel>
          ))}
        </ul>
      )}
    </div>
  );
}
